import { useRef, useEffect, useState } from 'react'
import { useContent } from '../../contexts/ContentContext'
import DividerPoint from '../ui/DividerPoint'
import { accentTextClass, resolveSectionTone } from '../../utils/sectionTone'

const STAGGER_DELAY_MS = 200
const OBSERVER_THRESHOLD = 0.2

/**
 * StatementSection — centered theatrical lines.
 * Content (contentKey, default "statement"):
 * {
 *   id?,
 *   variant?: 'light' | 'dark' | 'blue',  // blue = navy band
 *   lines: string[] | { text, accent?: true|'highlight'|'emphasis' }[]
 * }
 */
export default function StatementSection({ contentKey = 'statement' }) {
  const content = useContent()
  const data = content[contentKey] || content.statement
  const sectionRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true)
        })
      },
      { threshold: OBSERVER_THRESHOLD }
    )
    if (sectionRef.current) observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [])

  if (!data?.lines?.length) return null

  const sectionId =
    data.id || (contentKey === 'statement' ? 'statement' : contentKey)

  const isBlue =
    data.variant === 'blue' ||
    data.variant === 'dark' ||
    data.surface === 'blue' ||
    data.surface === 'navy'
  // Map blue/dark content onto sectionTone for drop-shadow choice
  const tone = resolveSectionTone(
    isBlue ? { ...data, variant: 'onDark', surface: 'blue' } : { ...data, variant: 'onLight' }
  )
  const onDark = tone === 'onDark'

  const lines = data.lines.map((line) =>
    typeof line === 'string' ? { text: line } : line
  )

  const defaultColor = onDark ? 'text-primary-on-dark' : 'text-color-primary'
  const dropClass = onDark ? 'effect-drop-display' : 'effect-drop-display-light'
  const markClass = onDark ? 'text-primary-on-dark' : 'text-color-primary'
  const sectionBg = onDark
    ? 'relative min-h-screen flex items-center justify-center snap-section overflow-hidden bg-color-primary'
    : 'relative h-screen flex items-center justify-center snap-section overflow-hidden'

  return (
    <section
      ref={sectionRef}
      id={sectionId}
      data-section="StatementSection"
      data-tone={tone}
      data-variant={onDark ? 'blue' : 'light'}
      className={sectionBg}
      data-testid={`statement-section-${contentKey}`}
    >
      <div className="relative z-10 w-full px-4 sm:px-6 md:px-0">
        <div
          className={`max-w-4xl mx-auto text-center transition-all duration-1000 ease-out
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
        >
          <div
            className={`mb-2 md:mb-6 ${markClass} transition-all duration-1000 delay-200
            ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}
          >
            <svg className="w-8 h-8 md:w-16 md:h-16 mx-auto" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
            </svg>
          </div>

          {lines.map((line, i) => {
            const colorCls =
              accentTextClass(line.accent) ||
              (line.tone === 'highlight'
                ? 'text-color-highlight'
                : line.tone === 'emphasis'
                  ? 'text-color-emphasis'
                  : defaultColor)
            return (
              <p
                key={i}
                className={`leading-relaxed italic ${dropClass} ${colorCls} ${
                  i > 0 ? 'mt-3 md:mt-8' : ''
                }
                typography-h2-mobile
                sm:typography-h2
                lg:typography-h2-large
                transition-all duration-1000 ease-out`}
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(2rem)',
                  transitionDelay: `${500 + i * STAGGER_DELAY_MS}ms`,
                }}
              >
                {line.text}
              </p>
            )
          })}

          {!onDark ? (
            <DividerPoint size="lg" spacing="md" isVisible={isVisible} />
          ) : null}
        </div>
      </div>
    </section>
  )
}
